/**
 * Company calendar rules. Weekday/weekend is derived from the date, never stored; holidays are
 * National or Company, and a Company holiday still counts as a working day when prorating salary.
 * A module store rather than props because render-time callers read it synchronously; until the
 * first fetch it reads as "no holidays".
 */
import { useSyncExternalStore } from 'react'

import { addDays, isWeekend } from './dates'

export type HolidayKind = 'National' | 'Company'

export interface Holiday {
  /** 'YYYY-MM-DD'. One holiday per date. */
  day: string
  holiday_kind: HolidayKind
  title: string
}

/** A company day's title when nobody typed one (the server refuses a blank): ours in org 1, the plain label for a customer. */
export function defaultCompanyTitle(isCustomer: boolean | undefined, companyLabel: string): string {
  return isCustomer === true ? companyLabel : 'TRG'
}

/** What a day's banner adds after its kind; null when the title would only repeat the kind or name nothing. */
export function holidayNote(holiday: Pick<Holiday, 'holiday_kind' | 'title'>, companyLabel: string): string | null {
  if (holiday.title === 'TRG') return null
  if (holiday.holiday_kind === 'Company' && (holiday.title === companyLabel || holiday.title === '')) return null
  return holiday.title
}

/* ------------------------------------------------------------------ store */

let holidays: Holiday[] = []
let index = new Map<string, Holiday>()
const listeners = new Set<() => void>()

/** Replace the cache; only `useHolidays()` writes here. */
export function primeHolidays(rows: Holiday[]): void {
  const next = [...rows].sort((a, b) => a.day.localeCompare(b.day))
  const same =
    next.length === holidays.length &&
    next.every((h, i) => h.day === holidays[i]!.day && h.holiday_kind === holidays[i]!.holiday_kind && h.title === holidays[i]!.title)
  if (same) return
  holidays = next
  index = new Map(holidays.map((h) => [h.day, h]))
  listeners.forEach((fn) => fn())
}

function subscribe(fn: () => void): () => void {
  listeners.add(fn)
  return () => listeners.delete(fn)
}

function getHolidays(): Holiday[] {
  return holidays
}

/** Re-renders the caller whenever the calendar is refetched. */
export function useHolidayList(): Holiday[] {
  return useSyncExternalStore(subscribe, getHolidays, getHolidays)
}

/* ------------------------------------------------------------------ rules */

export function holidayOn(date: string): Holiday | undefined {
  return index.get(date)
}

function isHoliday(date: string): boolean {
  return index.has(date)
}

/** A weekday that is no holiday of either kind; mirrors the server's `_working_days`, whose answer is stored. */
export function isWorkingDay(date: string): boolean {
  return !isWeekend(date) && !isHoliday(date)
}

/**
 * Working days in an inclusive range, what a leave request is charged. Only "Full Time" staff
 * skip holidays; the default false matches the server's `_working_days`.
 */
export function workingDays(start: string, end: string, skipHolidays = false): number {
  let count = 0
  for (let d = start; d <= end; d = addDays(d, 1)) {
    if (skipHolidays ? isWorkingDay(d) : !isWeekend(d)) count += 1
  }
  return count
}

/** Mirrors the server's `_skips_holidays`: any work type but Full Time is charged for holidays. */
export const skipsHolidays = (workType: string | undefined): boolean =>
  workType === 'Full Time'

/** Tailwind shade for a date cell, shared so weekend, company and national days look the same everywhere. */
export function dayTone(date: string): string {
  const holiday = index.get(date)
  if (holiday?.holiday_kind === 'Company') return 'bg-sky-100 dark:bg-sky-950/40'
  if (holiday) return 'bg-slate-300/70 dark:bg-slate-600/50'
  if (isWeekend(date)) return 'bg-slate-100 dark:bg-slate-700/40'
  return ''
}

export type NonWorkingKind = 'company' | 'holiday' | 'weekend'

/** `dayTone`'s split as data, for a caller that paints its own overlay. */
export function nonWorkingKind(date: string): NonWorkingKind | null {
  const holiday = index.get(date)
  if (holiday?.holiday_kind === 'Company') return 'company'
  if (holiday) return 'holiday'
  if (isWeekend(date)) return 'weekend'
  return null
}
